import { DEFAULT_FORM, GoalActivity, MAX_ACTIVITIES, NO_ACTIVITY, isDuplicateActivity } from './smartGoalUtils';

export type SmartGoalScreen = 'start' | 'celebrate' | 'build' | 'review' | 'success';

export type SmartGoalState = {
  screen: SmartGoalScreen;
  activities: GoalActivity[];
  form: GoalActivity;
  // Index of the activity being edited on the build screen, or null when adding a new one.
  editingIdx: number | null;
  // Which celebrate copy to show after a form submit.
  lastChange: 'added' | 'updated';
  deleteConfirmIdx: number | null;
  leaveWarningOpen: boolean;
  saving: boolean;
  saveError: string | null;
};

export type SmartGoalAction =
  | { type: 'setForm'; patch: Partial<GoalActivity> }
  | { type: 'submitForm' }
  | { type: 'addAnother' }
  | { type: 'review' }
  | { type: 'edit'; index: number }
  | { type: 'requestDelete'; index: number }
  | { type: 'cancelDelete' }
  | { type: 'confirmDelete' }
  | { type: 'requestLeave' }
  | { type: 'cancelLeave' }
  | { type: 'saveStarted' }
  | { type: 'saveFailed'; message: string }
  | { type: 'saveSucceeded' }
  | { type: 'reset' };

export const initialSmartGoalState: SmartGoalState = {
  screen: 'start',
  activities: [],
  form: { ...DEFAULT_FORM },
  editingIdx: null,
  lastChange: 'added',
  deleteConfirmIdx: null,
  leaveWarningOpen: false,
  saving: false,
  saveError: null,
};

export const canAddMore = (state: SmartGoalState) => state.activities.length < MAX_ACTIVITIES;

export const hasProgress = (state: SmartGoalState) =>
  state.activities.length > 0 || state.form.activityId !== NO_ACTIVITY;

export const isFormDuplicate = (state: SmartGoalState) =>
  isDuplicateActivity(state.activities, state.form, state.editingIdx);

export const canSubmitForm = (state: SmartGoalState) =>
  state.form.activityId !== NO_ACTIVITY && !isFormDuplicate(state) && (state.editingIdx !== null || canAddMore(state));

const freshForm = { form: { ...DEFAULT_FORM }, editingIdx: null };

export const smartGoalReducer = (state: SmartGoalState, action: SmartGoalAction): SmartGoalState => {
  switch (action.type) {
    case 'setForm':
      return { ...state, form: { ...state.form, ...action.patch } };

    case 'submitForm': {
      if (!canSubmitForm(state)) return state;
      if (state.editingIdx !== null) {
        const activities = state.activities.map((a, i) => (i === state.editingIdx ? { ...state.form } : a));
        return { ...state, ...freshForm, activities, screen: 'celebrate', lastChange: 'updated' };
      }
      return {
        ...state,
        ...freshForm,
        activities: [...state.activities, { ...state.form }],
        screen: 'celebrate',
        lastChange: 'added',
      };
    }

    case 'addAnother':
      if (!canAddMore(state)) return state;
      return { ...state, ...freshForm, screen: 'build' };

    case 'review':
      return { ...state, ...freshForm, screen: 'review', saveError: null };

    case 'edit':
      if (!state.activities[action.index]) return state;
      return { ...state, screen: 'build', editingIdx: action.index, form: { ...state.activities[action.index] } };

    case 'requestDelete':
      return { ...state, deleteConfirmIdx: action.index };

    case 'cancelDelete':
      return { ...state, deleteConfirmIdx: null };

    case 'confirmDelete': {
      const idx = state.deleteConfirmIdx;
      if (idx === null) return state;
      const activities = state.activities.filter((_, i) => i !== idx);

      // Nothing left to build on or review — start over with an empty form.
      if (activities.length === 0) {
        return { ...state, ...freshForm, activities, deleteConfirmIdx: null, screen: 'start' };
      }

      // Keep the edit pointing at the same activity after the list shifts.
      if (state.editingIdx === idx) {
        return { ...state, ...freshForm, activities, deleteConfirmIdx: null };
      }
      const editingIdx = state.editingIdx !== null && state.editingIdx > idx ? state.editingIdx - 1 : state.editingIdx;
      return { ...state, activities, editingIdx, deleteConfirmIdx: null };
    }

    case 'requestLeave':
      return { ...state, leaveWarningOpen: true };

    case 'cancelLeave':
      return { ...state, leaveWarningOpen: false };

    case 'saveStarted':
      return { ...state, saving: true, saveError: null };

    case 'saveFailed':
      return { ...state, saving: false, saveError: action.message };

    case 'saveSucceeded':
      return { ...state, saving: false, saveError: null, screen: 'success' };

    case 'reset':
      return initialSmartGoalState;

    default:
      return state;
  }
};
